<template>
  <div
    class="view">
    <HomeNavigation />

    <div
      class="home-content">
      <div
        class="collections-header">
        <div class="collections-header-left">
          <div class="collections-header-name">
            Get to know Byblio
          </div>
        </div>
      </div>

      <div class="tab-container">
        <div class="tab" />
        <div
          v-for="(tab, index) in tabs"
          :key="index"
          class="tab"
          :class="{ 'active-tab': activeTab === index }"
          @click="activeTab = index">
          {{ tab }}
        </div>
        <div class="filler-tab" />
      </div>
      <!-- COLLECTIONS -->
      <div class="tab-content" v-show="activeTab === 0">
        Everything in Byblio is stored in collections. When viewing a collection, you can switch between cards and notes in that collection via 'Cards' and 'Notes' on top of the screen.
        <br><br>
        Your collections appear in the sidebar.
        <br><br>
        Manage your collections via 1) the 'manage collections' option in the sidebar (which appears when you hover 'Collections' in the sidebar),
        or 2) via 'Home' <i class="mdi mdi-arrow-right" /> 'Collections'.<br>
        On the collection management page, you can add, move, rename, and delete collections. Using the
        <i class="mdi mdi-eye" /> icon, you can hide a collection, meaning it will not show up in the sidebar (unless it is the currently selected collection).
      </div>
      <!-- CARDS -->
      <div class="tab-content" v-show="activeTab === 1">
        In Byblio, you can store both documents and links to webpages. You store those in so-called <em>cards</em>,
        to which you can add meta information about the document or webpage. Store and organize your cards in <em>collections</em>.
        <br><br>
        Various pieces of meta information can be stored in your card. Besides a title, those include the following.<br><br>
        <div class="card-attributes">
          <strong>Section.</strong> Use sections to organize your cards within your collections. You could, for instance,
          use an 'Unread' section or replicate chapters of a paper or article you are writing.
          Cards with the same section are grouped together when viewing lists of cards.
          <br><br>
          <strong>Description.</strong> A versatile attribute that you can use to store any textual content.
          <br><br>
          <strong>Stars.</strong> Besides the default 'no stars', indicated by a semi-transparent <i class="mdi mdi-star-outline inactive" />, a card
          can have three levels of stars (indicated by <i class="mdi mdi-star-outline" />, <i class="mdi mdi-star-half" />,
          and a <i class="mdi mdi-star" />). Use stars to indicate importance or favourites.
          You can view all cards with the same star level(s) via <span class="sidebar-header">Cards by stars</span> in the sidebar.
          <br><br>
          <strong>Keywords.</strong> Whereas sections add <em>within-collection</em> organization possibilities,
          keywords add <em>across-collection</em> organization possibilities. View all cards that share a keyword or keywords
          via <span class="sidebar-header">Cards by keyword</span> in the sidebar.
        </div>
        <br>
        All card attributes can be edited by going to a card. When viewing a list of cards, you can edit the stars for a card by clicking on the star
        icon on the right. Via <i class="mdi mdi-dots-horizontal" /> on the left or by right-clicking on a card in the list, you open up a menu with further options.
        <br><br>
        When viewing a list of cards, go to the file or webpage stored in a card by 1) clicking on the <i class="mdi mdi-paperclip mdi-rotate-45" /> or
        <i class="mdi mdi-open-in-new" /> icon, 2) clicking <div class="go-to-item">Go to file/webpage</div>, or 3) right-click and choose 'Open file/webpage'.
        Alternatively, you can go to the card first and subsequently click on the attached item.<br>
        <span class="tip">Tip: you can open a card, file, or webpage in the background (i.e. without navigating there) by holding CTRL while you click.</span>
        <br><br>
        When viewing a card or list of cards, you see the
        <div class="add-button">
          <i class="mdi mdi-plus mdi-24px" />
        </div> button in the bottom-right corner. Via this button you can add a card. You can add multiple files at once by hovering this button
        and picking the appearing
        <div class="add-button">
          <i class="mdi mdi-plus-box-multiple mdi-24px" />
        </div>.
        <br><br><br><br><br><br><br><br>
      </div>
      <!-- NOTES -->
      <div class="tab-content" v-show="activeTab === 2">
        Similar to cards, you can star notes and organize notes into sections.
        <br><br>
        When viewing a list of notes, you can edit the stars for a note by clicking on the star icon on the right. Via <i class="mdi mdi-dots-horizontal" />
        on the left or by right-clicking on a note in the list, you open up a menu with further options.
        <br><br>
        When viewing a note or list of notes, you see the
        <div class="add-button">
          <i class="mdi mdi-plus mdi-24px" />
        </div> button in the bottom-right corner. Via this button you can add a note.
      </div>
      <!-- VIEWER -->
      <div class="tab-content" v-show="activeTab === 3">
        The viewer is where you can view and annotate your files. It opens whenever you open a file. Return to your cards or notes via 'Cards' or 'Notes'
        in the top-left of the viewer.
        <br><br>
        When viewing your cards or notes, you can return to the viewer via 'Viewer' on top of the screen.
      </div>
      <!-- EXTENSION -->
      <div class="tab-content" v-show="activeTab === 4">
        If you use the Chrome browser, there is an extension available that lets you quickly save a webpage you are currently visiting to Byblio.
        Like Byblio, it is in the beta version, which means that Chrome only lets authorized users find and install the extension. Therefore,
        please send me the email address you use to login to the Chrome Webstore, and I will add you as authorized user!
      </div>
      <!-- TIPS -->
      <div class="tab-content" v-show="activeTab === 5">
        When viewing lists of cards or notes, use the top bar to sort on title, cards, or date last modified.
        <br><br>
        When opening a card, file, webpage, or note, holding CTRL on click opens the card/file/webpage/note in the background (i.e. without navigating there).
        <br><br>
        You can pin a card or a note by hovering on a tab and clicking on the <i class="mdi mdi-pin-outline" /> icon that appears. Next time you open Byblio, the cards and notes you pinned
        will be opened by default, and any associated files will be opened in the viewer.
      </div>
    </div>
  </div>
</template>

<script>
import HomeNavigation from '@/components/AppView/HomeNavigation.vue';

export default {
  name: 'Home',
  components: {
    HomeNavigation,
  },
  data() {
    return {
      activeTab: 0,
      tabs: [
        'Collections',
        'Cards',
        'Notes',
        'Viewer',
        'Extension',
        'Tips',
      ],
    };
  },
  methods: {
    //
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/fonts';
@import '~styles/layouts';

.view {
  position: relative;
  height: 100vh;
  max-height: 100vh;
  overflow-y: hidden;
  overflow-x: hidden;
}
.home-content {
  position: relative;
  height: calc(100% - 2.875rem); // minus collection tabs height
}
.collections-header {
  position: sticky;
  top: 10px;
  display: flex;
  width: 100%;
  padding: 0.125rem;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  height: 1.5rem;
  font-size: 0.7rem;
  color: rgba($black, 0.75);
  background-color: rgba($primary, 0.25);
  user-select: none;
}
.collections-header div {
  display: inline-flex;
}
.collections-header-name {
  padding-left: 0.25rem;
}
.collections-header-left {
  flex-basis: 50%;
  display: flex;
  padding-left: 2rem;
}
.sub-header {
  position: sticky;
  top: 0;
  font-family: $headerFont;
  font-size: 0.8rem;
  width: calc(100% - 1rem);
  height: 1.25rem;
  border-bottom: 1px solid $primary;
  padding: 0 2rem;
  margin: 0 0.5rem;
  background: #ffffff;
  z-index: 1;
}
.tab-content {
  width: calc(100% - 1rem);
  height: calc(100vh - 10rem);
  padding: 1rem 30% 1rem 2rem;
  font-size: 0.9rem;
  text-align: justify;
  overflow-y: auto;
}
.italic {
  font-style: italic;
}
.tab-container {
  margin: 2.5rem 0 0.5rem;
  display: flex;
  flex-wrap: nowrap;
  user-select: none;
}
.tab:first-child {
  width: 0.5rem;
  cursor: default;
}
.tab {
  padding: 0.25rem 0.75rem;
  text-align: center;
  cursor: pointer;
  width: 8rem;
  flex: 0 0 auto;
  border: 1px solid transparent;
  border-bottom: 1px solid $primary;
  transition: all 0.2s;
}
.filler-tab {
  flex: 1 1 auto;
  border-bottom: 1px solid $primary;
}
.active-tab {
  border: 1px solid $primary;
  color: $secondary-dark;
  border-bottom: 1px solid transparent;
}
.card-attributes {
  padding-left: 2rem;
}
.inactive {
  color: rgba(174, 174, 174, 0.5);
}
.sidebar-header {
  font-variant: small-caps;
  font-weight: 600;
  font-size: 0.8rem;
  color: $secondary;
  padding-left: 0.25rem;
  white-space: nowrap;
  user-select: none;
}
.add-button {
  height: 1.5rem;
  width: 1.5rem;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: $white;
  box-shadow: 0.5px 0.5px 2px 1px $primary;
  border-radius: 50%;
  background-color: $secondary-dark;
  z-index: 5;
  cursor: default;
  transition: opacity 0.3s;
  line-height: 1.5rem;
}
.add-button .mdi {
  transform: scale(0.5);
}

.go-to-item {
  line-height: 1.7rem;
  border-radius: 0.25rem 0 0;
  background-color: $secondary;
  height: auto !important;
  width: auto !important;
  display: inline;
  padding: 0.25rem;
  white-space: nowrap;
  text-align: center;
  font-size: 0.85rem;
  color: $primary-light;
  font-family: $headerFont;
  cursor: default;
  border-bottom: 1px solid #eeeeee;
  border-top: 1px solid #eeeeee;
  opacity: 1 !important;
  user-select: none;
}
.tip {
  font-size: 0.8rem;
  font-style: italic;
  color: #a0a0a0;
}
</style>
